import React from 'react';
import { Button, Box, Typography } from '@drone-js/core';
import { routeTo } from 'store/slice/navigation';
import { getHomeUrl, getImageUrl } from 'core/utils/activityUrlGenerator';
import { useDispatch, useSelector } from 'react-redux';
import { resetState } from 'store/storeUtils';
import ContentArea from 'component/common/ContentArea/ContentArea';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesErrorComponentSeenEventActivitiesFieldsV3,
    ActivitiesPageContentErrorButtonClickEventActivitiesFieldsV3,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesErrorComponentSeenEventActivitiesManifestV3,
    ActivitiesPageContentErrorButtonClickEventActivitiesManifestV3,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { ErrorPageActionElementValue } from 'component/page/error/ActivityStandardErrorPage';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';

interface Props {
    error: string;
    buttonText: string;
    contentStatus?: PageContentStatus;
}

const NoDetailsErrorPage: React.FC<Props> = React.memo((props) => {
    const dispatch = useDispatch();
    const cms = useSelector(selectCmsFromBootstrap);

    React.useEffect(() => {
        trackElementEvent<ActivitiesErrorComponentSeenEventActivitiesFieldsV3>(
            ActivitiesErrorComponentSeenEventActivitiesManifestV3.ids,
            {},
            {
                action_element_value: props.contentStatus?.toString(),
            },
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run seen tracking once
    }, []);

    const onButtonClick = () => {
        trackElementEvent<ActivitiesPageContentErrorButtonClickEventActivitiesFieldsV3>(
            ActivitiesPageContentErrorButtonClickEventActivitiesManifestV3.ids,
            {},
            { action_element_value: ErrorPageActionElementValue.TechnicalError },
        );
        const homeUrl = getHomeUrl();
        resetState(dispatch);
        dispatch(routeTo({ path: homeUrl }));
    };

    return (
        <ContentArea mx="auto" width="100%" display="flex" alignItems="center" justifyContent="center">
            <div data-element-name="activity-no-details-error-page">
                <div className="Error__StandardContainer__Image">
                    <Box display="flex" py="16" justifyContent="center">
                        <ActivityImage
                            data-testid="activities-error-image"
                            alt={cms[126985]}
                            src={getImageUrl('Error-Disable.svg')}
                            width="140px"
                            loading="immediate"
                            responsive="original"
                        />
                    </Box>
                </div>
                <Box px="32" display="flex" justifyContent="center">
                    <Typography variant="body-default" key="errorMessage" textAlign="center">
                        {props.error}
                    </Typography>
                </Box>
                <Box px="32" py="16" display="flex" justifyContent="center">
                    <Button
                        data-testid="try-another-activity-btn"
                        mx="12"
                        variant="label"
                        hierarchy="secondary"
                        width="parent-fit"
                        onClick={onButtonClick}
                        label={props.buttonText}
                    />
                </Box>
            </div>
        </ContentArea>
    );
});
NoDetailsErrorPage.displayName = 'NoDetailsErrorPage';

export default NoDetailsErrorPage;
